/**
 * Copyright Motili Inc., 2024 All Rights Reserved
 */

import { useState, useEffect, useRef } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import Config from 'Config';

export function useGeocoder() {
    const geocodingLibrary = useMapsLibrary('geocoding');
    const [geocoder, setGeocoder] = useState(null);

    useEffect(() => {
        if (!geocodingLibrary) return;
        setGeocoder(new geocodingLibrary.Geocoder());
    }, [geocodingLibrary]);

    return geocoder;
}

export function useAutocompleteService() {
    const placesLibrary = useMapsLibrary('places');
    const [autocompleteService, setAutocompleteService] = useState(null);

    useEffect(() => {
        if (!placesLibrary) return;
        setAutocompleteService(new placesLibrary.AutocompleteService());
    }, [placesLibrary]);

    return autocompleteService;
}

export function usePlacesService(mapId) {
    const map = useMap(mapId);
    const placesLibrary = useMapsLibrary('places');
    const [placesService, setPlacesService] = useState(null);

    useEffect(() => {
        if (!placesLibrary || !map) return;
        setPlacesService(new placesLibrary.PlacesService(map));
    }, [placesLibrary, map]);

    return placesService;
}

export function useGoogleMapsStatus() {
    const geocodingLibrary = useMapsLibrary('geocoding');
    const [status, setStatus] = useState(null);

    useEffect(() => {
        if (!geocodingLibrary) return;
        setStatus({
            OK: geocodingLibrary.GeocoderStatus.OK,
            ZERO_RESULTS: geocodingLibrary.GeocoderStatus.ZERO_RESULTS,
        });
    }, [geocodingLibrary]);

    return status;
}

export function useDrawingManager(mapId) {
    const map = useMap(mapId);
    const drawing = useMapsLibrary('drawing');
    const drawingManagerRef = useRef(null);

    useEffect(() => {
        if (!map || !drawing) return;

        const drawingManager = new drawing.DrawingManager({
            drawingMode: null,
            drawingControl: false,
            polygonOptions: {
                editable: true,
                draggable: true,
            },
        });

        drawingManagerRef.current = drawingManager;
        drawingManager.setMap(map);

        return () => {
            drawingManager.setMap(null);
            drawingManagerRef.current = null;
        };
    }, [map, drawing]);

    return {
        setDrawingMode: mode => drawingManagerRef.current?.setDrawingMode(mode),
        getDrawingManager: () => drawingManagerRef.current,
    };
}

// Modified utility functions that use the hooks
export const geocodeAddress = (geocoder, status, address) => {
    if (!geocoder || !status)
        return Promise.reject(new Error('Services not loaded'));

    return new Promise((resolve, reject) => {
        geocoder.geocode({ address }, (results, geocodeStatus) => {
            if (geocodeStatus !== status.OK) {
                const error = new Error(`Unable to locate address: ${address}`);
                error.geocoderStatus = geocodeStatus;
                return reject(error);
            }
            return resolve(results);
        });
    });
};

export const fetchPredictions = (
    autocompleteService,
    status,
    value,
    filterPredictions, // Optional filter function
    options = {}
) => {
    if (!autocompleteService || !status)
        return Promise.reject(new Error('Services not loaded'));

    return new Promise((resolve, reject) => {
        if (!value.length) resolve([]);

        const _options = {
            ...options,
            input: value,
        };

        autocompleteService.getPlacePredictions(
            _options,
            (predictions, predictionStatus) => {
                if (predictionStatus === status.ZERO_RESULTS) {
                    return resolve([]);
                }
                if (predictionStatus !== status.OK) {
                    return reject(
                        new Error(`Google return status ${predictionStatus}`)
                    );
                }
                const filteredPredictions =
                    typeof filterPredictions !== 'undefined'
                        ? filterPredictions(predictions)
                        : predictions;

                const suggestions = filteredPredictions?.map((p, idx) => ({
                    index: idx,
                    placeId: p.place_id,
                    description: p.description,
                    mainText: p.structured_formatting.main_text,
                    secondaryText: p.structured_formatting.secondary_text,
                    terms: p.terms,
                    types: p.types,
                }));

                return resolve(suggestions);
            }
        );
    });
};

export const getAddressDetail = (result, config) =>
    new Promise((resolve, reject) => {
        try {
            const place = {
                id: result.place_id,
                formattedAddress: result.formatted_address,
                position: {
                    lat: result.geometry.location.lat(),
                    lng: result.geometry.location.lng(),
                },
                components: {},
            };

            result.address_components.forEach(component => {
                const type = component.types[0];
                switch (type) {
                    case 'street_number':
                        place.components.streetNumber = component.long_name;
                        break;
                    case 'route':
                        place.components.streetName = config[type]
                            ? component[config[type]]
                            : component.long_name;
                        break;
                    case 'locality':
                        place.components.city = component.long_name;
                        break;
                    case 'administrative_area_level_2':
                        place.components.county = component.long_name;
                        break;
                    case 'administrative_area_level_1':
                        place.components.state = component.short_name;
                        place.components.stateName = component.long_name;
                        break;
                    case 'country':
                        place.components.country = component.short_name;
                        place.components.countryName = component.long_name;
                        break;
                    case 'postal_code':
                        place.components.zip = component.long_name;
                        break;
                    default:
                        break;
                }
            });

            place.components.deliveryLine = `${
                place.components.streetNumber
                    ? place.components.streetNumber
                    : ''
            } ${
                place.components.streetName ? place.components.streetName : ''
            }`.trim();

            switch (result.types[0]) {
                case 'street_number':
                case 'route':
                    place.type = 'street';
                    break;
                case 'locality':
                    place.type = 'city';
                    break;
                case 'administrative_area_level_2':
                    place.type = 'county';
                    break;
                case 'administrative_area_level_1':
                    place.type = 'state';
                    break;
                case 'country':
                    place.type = 'country';
                    break;
                case 'postal_code':
                    place.type = 'zip';
                    break;
                case 'street_address':
                    place.type = 'address';
                    break;
                default:
                    place.type = `unknown: ${result.types[0]}`;
                    break;
            }
            resolve(place);
        } catch (e) {
            reject(e);
        }
    });

export const getPlaceDetail = (geocoder, status, placeId) =>
    new Promise((resolve, reject) => {
        geocoder.geocode({ placeId }, (results, placeStatus) => {
            if (placeStatus !== status.OK) {
                return reject(new Error(placeStatus));
            }

            return resolve(results[0]);
        });
    });

export function getDistributorInventoryIcon(distributor) {
    switch (distributor) {
        case 'Goodman':
            return {
                url: `${Config.baseCDNURL}images/distributionAndInventory/Goodman COD.svg`,
            };
        case 'Ferguson':
            return {
                url: `${Config.baseCDNURL}images/distributionAndInventory/Ferguson.svg`,
            };
        case 'Johnstone':
            return {
                url: `${Config.baseCDNURL}images/distributionAndInventory/Johnstone.svg`,
            };
        case 'Thermal':
            return {
                url: `${Config.baseCDNURL}images/distributionAndInventory/Thermal.svg`,
            };
        case 'Williams':
            return {
                url: `${Config.baseCDNURL}images/distributionAndInventory/Williams.svg`,
            };
        case 'East Coast Metals':
            return {
                url: `${Config.baseCDNURL}images/distributionAndInventory/East Coast Metals.svg`,
            };
        case 'Budget Air Supply':
            return {
                url: `${Config.baseCDNURL}images/distributionAndInventory/Budget.svg`,
            };
        default:
            return {
                url: `${Config.baseCDNURL}images/distributionAndInventory/Generic.svg`,
            };
    }
}

export function getDistributorIcon(distributor, isFavorite = false) {
    const iconStyle = isFavorite ? 'distributorFavorite' : 'distributor';

    switch (distributor) {
        case 'Goodman':
            return createIcon(iconStyle, 'red');
        case 'Ferguson':
            return createIcon(iconStyle, 'teal');
        case 'Johnstone':
            return createIcon(iconStyle, 'blue');
        case 'Thermal':
            return createIcon(iconStyle, 'purple');
        case 'Williams':
            return createIcon(iconStyle, 'yellow');
        case 'East Coast Metals':
            return createIcon(iconStyle, 'green');
        case 'Budget Air Supply':
            return createIcon(iconStyle, 'orange');
        default:
            return createIcon(iconStyle, 'black');
    }
}

/**
 * gets predefined icons
 * @function getDefinedIcon
 * @param  {string} value {icon type}
 * @return {type} {google maps icon object}
 */
export function getDefinedIcon(value) {
    switch (value) {
        case 'contractorNoReply':
            return createIcon('man', 'mapMarkerBlue');
        case 'contractorAccepted':
            return createIcon('man', 'green');
        case 'contractorDeclined':
            return createIcon('man', 'red');
        case 'contractorSomeDeclined':
            return createIcon('man', 'mapMarkerYellow');
        case 'contractorDispatchingDisabled':
            return createIcon('man', 'white');
        case 'contractorDispatchStatusUnknown':
            return createIcon('man', 'mediumGray');
        case 'property':
            return createIcon('property', 'blue');
        case 'searchLocation':
            return createIcon('marker', 'red');
        case 'ncfNearContractor':
            return createIcon('man', 'white');
        case 'contractor':
            return createIcon('man', 'red');
        default:
            return createIcon('marker', 'white');
    }
}

/**
 * Creates a google maps svg icon for use in a marker
 * @function createIcon
 * @param  {string} style {the style of the icon}
 * @param  {string} color {fill color of the icon}
 * @return {{url: string}} {google maps icon object}
 */
function createIcon(style, color) {
    switch (style) {
        case 'marker': {
            const svg = `<?xml version="1.0" encoding="UTF-8"?>
            <svg width="32px" height="53px" viewBox="0 0 32 53" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                <!-- Generator: Sketch 55.2 (78181) - https://sketchapp.com -->
                <title>Combined Shape</title>
                <desc>Created with Sketch.</desc>
                <g id="Symbols" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                    <g id="Area-Marker" fill="${Config.color[color]}" stroke="#333333" stroke-width="0.75">
                        <path d="M16.0276614,52.2733597 C26.4273602,34.9454529 31.625,22.6807424 31.625,15.5341549 C31.625,5.98573361
                            24.8098984,0.375 16.0282284,0.375 C7.23087267,0.375 0.375,5.99331892 0.375,15.5341549 C0.375,22.6805215
                            5.59107885,34.9453175 16.0276614,52.2733597 Z M16,25.375 C10.8223305,25.375 6.625,21.1776695 6.625,16
                            C6.625,10.8223305 10.8223305,6.625 16,6.625 C21.1776695,6.625 25.375,10.8223305 25.375,16 C25.375,21.1776695
                            21.1776695,25.375 16,25.375 Z" id="Combined-Shape"></path>
                    </g>
                </g>
            </svg>`;

            return {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    svg
                )}`,
            };
        }
        case 'property': {
            const svg = `<?xml version="1.0" encoding="UTF-8"?>
            <svg width="31px" height="36px" viewBox="0 0 31 36" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                <!-- Generator: Sketch 55.2 (78181) - https://sketchapp.com -->
                <title>Property Marker</title>
                <desc>Created with Sketch.</desc>
                <defs>
                    <path d="M20.3333333,14.3416667 L20.3333333,20.3125 C20.3333333,20.8666667 19.8875,21.3125 19.3333333,21.3125 L14.5,21.3125
                        C14.225,21.3125 14,21.0875 14,20.8125 L14,16.1458333 C14,15.8708333 13.775,15.6458333 13.5,15.6458333 L10.5,15.6458333
                        C10.225,15.6458333 10,15.8708333 10,16.1458333 L10,20.8125 C10,21.0875 9.775,21.3125 9.5,21.3125 L4.66666667,21.3125
                        C4.1125,21.3125 3.66666667,20.8666667 3.66666667,20.3125 L3.66666667,14.3416667 C3.66666667,14.1916667 3.73333333,14.05
                        3.85,13.9541667 L11.6833333,7.50416667 C11.8666667,7.35416667 12.1333333,7.35416667 12.3208333,7.50416667
                        L20.1541667,13.9541667 C20.2666667,14.05 20.3333333,14.1916667 20.3333333,14.3416667 Z M23.8166667,11.8041667
                        L20.3333333,8.93333333 L20.3333333,3.1625 C20.3333333,2.8875 20.1083333,2.6625 19.8333333,2.6625 L17.5,2.6625
                        C17.225,2.6625 17,2.8875 17,3.1625 L17,6.1875 L13.2708333,3.11666667 C12.5333333,2.50833333 11.4666667,2.50833333
                        10.7291667,3.11666667 L0.183333333,11.8041667 C-0.0291666667,11.9791667 -0.0583333333,12.2958333 0.116666667,12.5083333
                        L1.17916667,13.8 C1.35416667,14.0125 1.67083333,14.0416667 1.88333333,13.8666667 L11.6833333,5.79583333
                        C11.8666667,5.64583333 12.1333333,5.64583333 12.3208333,5.79583333 L22.1208333,13.8666667 C22.3333333,14.0416667
                        22.65,14.0125 22.825,13.8 L23.8875,12.5083333 C24.0625,12.2916667 24.0291667,11.9791667 23.8166667,11.8041667
                        L23.8166667,11.8041667 Z" id="path-1"></path>
                </defs>
                <g id="Property-Marker" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                    <g id="Group-3">
                        <path d="M15.5,35.2351017 L19.2181822,28.625 L29,28.625 C29.8974627,28.625 30.625,27.8974627 30.625,27 L30.625,2
                            C30.625,1.10253728 29.8974627,0.375 29,0.375 L2,0.375 C1.10253728,0.375 0.375,1.10253728 0.375,2 L0.375,27
                            C0.375,27.8974627 1.10253728,28.625 2,28.625 L11.7818178,28.625 L15.5,35.2351017 Z"
                            id="Combined-Shape" stroke="#333333" stroke-width="0.75" fill="${Config.color[color]}"></path>
                        <g id="Solid/home" transform="translate(3.500000, 2.500000)">
                            <mask id="mask-2" fill="white">
                                <use xlink:href="#path-1"></use>
                            </mask>
                            <g id="Mask"></g>
                            <g id="Color/Black" mask="url(#mask-2)" fill="#FFFFFF" fill-rule="evenodd">
                                <rect id="Black" x="0" y="0" width="24" height="24"></rect>
                            </g>
                        </g>
                    </g>
                </g>
            </svg>`;

            return {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    svg
                )}`,
            };
        }
        case 'distributor': {
            const svg = `<?xml version="1.0" encoding="UTF-8"?>
            <svg width="31px" height="36px" viewBox="0 0 31 36" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                <!-- Generator: Sketch 55.2 (78181) - https://sketchapp.com -->
                <title>Distrobution Center</title>
                <desc>Created with Sketch.</desc>
                <g id="Distrobution-Center" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                    <g id="Group-2-Copy-2" fill="${Config.color[color]}" stroke="#333333" stroke-width="0.75">
                        <path d="M15.5,35.2351017 L19.2181822,28.625 L29,28.625 C29.8974627,28.625 30.625,27.8974627 30.625,27 L30.625,2
                            C30.625,1.10253728 29.8974627,0.375 29,0.375 L2,0.375 C1.10253728,0.375 0.375,1.10253728 0.375,2 L0.375,27
                            C0.375,27.8974627 1.10253728,28.625 2,28.625 L11.7818178,28.625 L15.5,35.2351017 Z" id="Combined-Shape-Copy-2"></path>
                    </g>
                    <path d="M27,19.574027 C27,20.3603699 26.3867716,21 25.6330233,21 L24.8938731,21 L24.6068158,21 C24.465293,21
                        24.3453886,20.8909536 24.3261103,20.7445897 C24.3235473,20.7247103 24.3187556,20.7057608 24.3158583,20.6861139
                        C24.314521,20.6780924 24.3128495,20.6703033 24.3116237,20.6623981 C24.1121546,19.432662 23.1113542,18.504838
                        21.9212254,18.4349692 C21.8735311,18.4320629 21.8260596,18.427994 21.7776967,18.427994 C20.4981968,18.427994
                        19.402565,19.4238267 19.2291717,20.7445897 C19.2098934,20.8909536 19.0899891,21 18.9484662,21 L13.6867149,21
                        L13.3995462,21 C13.2580233,21 13.138119,20.8909536 13.1188407,20.7445897 C12.952022,19.4736997 11.9307176,18.5063493
                        10.7139558,18.4349692 C10.66615,18.4320629 10.61879,18.427994 10.5704271,18.427994 C9.29103858,18.427994
                        8.19551828,19.4238267 8.02201353,20.7445897 C8.00273523,20.8909536 7.88283086,21 7.74119661,21 L6.65403396,21
                        L6.36675379,21 C5.61322838,21 5,20.3603699 5,19.574027 L5,9.4258567 C5,8.63963013 5.61322838,8 6.36675379,8
                        L6.65403396,8 L18.1326485,8 C18.8861739,8 19.4994023,8.63963013 19.4994023,9.4258567 L19.4994023,9.89726713
                        L19.7864596,9.89726713 L21.4049457,9.89726713 C21.753961,9.89726713 22.0860382,10.0357258 22.3416707,10.2876484
                        C22.3476882,10.2931124 22.3535943,10.2989251 22.3591661,10.3049703 C22.3767728,10.3229897 22.3940453,10.3425204
                        22.4113178,10.3622836 L26.5415552,14.9117721 C26.6016189,14.9676903 26.6572251,15.0296537 26.7070366,15.0955698
                        C26.7101568,15.0994062 26.7129427,15.1033588 26.71584,15.1074277 C26.9016026,15.3595829 27,15.6591698 27,15.9738697
                        L27,19.574027 Z M21.4483386,11.1645067 C21.3950725,11.1082397 21.3225282,11.0763861 21.2466407,11.0763861
                        L20.3566071,11.0763861 C20.200152,11.0763861 20.0732272,11.2087995 20.0732272,11.3720203 L20.0732272,14.8883585
                        C20.0732272,15.0515793 20.200152,15.1839927 20.3566071,15.1839927 L24.5712011,15.1839927 C24.6855337,15.1839927
                        24.7886113,15.1123802 24.8326283,15.0024038 C24.8767566,14.8924274 24.8533552,14.7655941 24.7730104,14.6807286
                        L21.4483386,11.1645067 Z M8.6,21.3500485 C8.6,21.2747909 8.60504304,21.200794 8.6130925,21.12767
                        C8.64053825,20.8807552 8.71375924,20.6479028 8.82296036,20.4362892 C9.15550976,19.7921384 9.82642745,19.35
                        10.5999515,19.35 C10.6464056,19.35 10.691696,19.3538793 10.7372772,19.3569827 C11.7760456,19.4280701 12.6,20.2935325
                        12.6,21.3500485 C12.6,22.4062735 11.7764335,23.271542 10.7380531,23.3430173 C10.6922779,23.3462177 10.6465026,23.35
                        10.5999515,23.35 C9.49717542,23.35 8.6,22.4528246 8.6,21.3500485 Z M21.8873742,19.2569827 C22.9260456,19.3280701
                        23.75,20.1935325 23.75,21.2500485 C23.75,22.3062735 22.9265305,23.171542 21.8881501,23.2430173 C21.8422779,23.2462177
                        21.7965996,23.25 21.7500485,23.25 C20.6472724,23.25 19.75,22.3528246 19.75,21.2500485 C19.75,21.1726573
                        19.755431,21.0966238 19.7639653,21.0215602 C19.7906352,20.7892896 19.8580373,20.5697236 19.9575403,20.3684871
                        C20.2841738,19.7071706 20.9639168,19.25 21.7500485,19.25 C21.7964056,19.25 21.841696,19.2538793 21.8873742,19.2569827 Z"
                        id="Combined-Shape" fill="#FFFFFF"></path>
                </g>
            </svg>`;

            return {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    svg
                )}`,
            };
        }
        case 'distributorFavorite': {
            const svg = `<?xml version="1.0" encoding="UTF-8"?>
            <svg width="36px" height="41px" viewBox="0 0 36 41" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                <!-- Generator: Sketch 55.2 (78181) - https://sketchapp.com -->
                <title>Distrobution Center Favorite</title>
                <desc>Created with Sketch.</desc>
                <g id="Distrobution-Center-Favorite" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                    <g id="Group-2-Copy-2" transform="translate(0.000000, 5.000000)" fill="${Config.color[color]}" stroke="#333333" stroke-width="0.75">
                        <path d="M15.5,35.2351017 L19.2181822,28.625 L29,28.625 C29.8974627,28.625 30.625,27.8974627 30.625,27 L30.625,2
                            C30.625,1.10253728 29.8974627,0.375 29,0.375 L2,0.375 C1.10253728,0.375 0.375,1.10253728 0.375,2 L0.375,27
                            C0.375,27.8974627 1.10253728,28.625 2,28.625 L11.7818178,28.625 L15.5,35.2351017 Z" id="Combined-Shape-Copy-2"></path>
                    </g>
                    <path d="M27,24.3101241 C27,25.0785373 26.3867716,25.7035831 25.6330233,25.7035831 L24.8938731,25.7035831 L24.6068158,25.7035831
                        C24.465293,25.7035831 24.3453886,25.5970231 24.3261103,25.4539965 C24.3235473,25.4345703 24.3187556,25.4160529
                        24.3158583,25.396854 C24.314521,25.3890153 24.3128495,25.3814039 24.3116237,25.3736789 C24.1121546,24.1719824
                        23.1113542,23.265314 21.9212254,23.1970383 C21.8735311,23.1941983 21.8260596,23.1902221 21.7776967,23.1902221
                        C20.4981968,23.1902221 19.402565,24.1633486 19.2291717,25.4539965 C19.2098934,25.5970231 19.0899891,25.7035831
                        18.9484662,25.7035831 L13.6867149,25.7035831 L13.3995462,25.7035831 C13.2580233,25.7035831 13.138119,25.5970231
                        13.1188407,25.4539965 C12.952022,24.2120844 11.9307176,23.2667908 10.7139558,23.1970383 C10.66615,23.1941983
                        10.61879,23.1902221 10.5704271,23.1902221 C9.29103858,23.1902221 8.19551828,24.1633486 8.02201353,25.4539965
                        C8.00273523,25.5970231 7.88283086,25.7035831 7.74119661,25.7035831 L6.65403396,25.7035831 L6.36675379,25.7035831
                        C5.61322838,25.7035831 5,25.0785373 5,24.3101241 L5,14.3933453 C5,13.6250457 5.61322838,13 6.36675379,13 L6.65403396,13
                        L18.1326485,13 C18.8861739,13 19.4994023,13.6250457 19.4994023,14.3933453 L19.4994023,14.854007 L19.7864596,14.854007
                        L21.4049457,14.854007 C21.753961,14.854007 22.0860382,14.9893086 22.3416707,15.2354871 C22.3476882,15.2408264
                        22.3535943,15.2465066 22.3591661,15.252414 C22.3767728,15.2700225 22.3940453,15.2891079 22.4113178,15.3084204
                        L26.5415552,19.7541747 C26.6016189,19.8088179 26.6572251,19.8693685 26.7070366,19.9337816 C26.7101568,19.9375305
                        26.7129427,19.941393 26.71584,19.9453691 C26.9016026,20.1917748 27,20.4845307 27,20.792055 L27,24.3101241 Z
                        M21.4483386,16.0923518 C21.3950725,16.0373678 21.3225282,16.0062405 21.2466407,16.0062405 L20.3566071,16.0062405
                        C20.200152,16.0062405 20.0732272,16.1356347 20.0732272,16.2951339 L20.0732272,19.7312949 C20.0732272,19.8907941
                        20.200152,20.0201883 20.3566071,20.0201883 L24.5712011,20.0201883 C24.6855337,20.0201883 24.7886113,19.9502086
                        24.8326283,19.8427398 C24.8767566,19.7352711 24.8533552,19.6113298 24.7730104,19.5283993 L21.4483386,16.0923518 Z
                        M8.6,26.04565 C8.6,25.9721084 8.60504304,25.8997987 8.6130925,25.828342 C8.64053825,25.5870572 8.71375924,25.3595141
                        8.82296036,25.1527256 C9.15550976,24.5232623 9.82642745,24.0912052 10.5999515,24.0912052 C10.6464056,24.0912052
                        10.691696,24.094996 10.7372772,24.0980287 C11.7760456,24.1674952 12.6,25.013224 12.6,26.04565 C12.6,27.0777917
                        11.7764335,27.9233309 10.7380531,27.9931765 C10.6922779,27.996304 10.6465026,28 10.5999515,28 C9.49717542,28 8.6,27.1232814
                        8.6,26.04565 Z M21.8873742,24.0003088 C22.9260456,24.0697753 23.75,24.9155041 23.75,25.9479301 C23.75,26.9800718
                        22.9265305,27.8256111 21.8881501,27.8954567 C21.8422779,27.8985841 21.7965996,27.9022801 21.7500485,27.9022801
                        C20.6472724,27.9022801 19.75,27.0255615 19.75,25.9479301 C19.75,25.8723035 19.755431,25.7980037 19.7639653,25.7246517
                        C19.7906352,25.4976771 19.8580373,25.2831175 19.9575403,25.0864695 C20.2841738,24.4402318 20.9639168,23.9934853
                        21.7500485,23.9934853 C21.7964056,23.9934853 21.841696,23.9972761 21.8873742,24.0003088 Z"
                        id="Combined-Shape" fill="#FFFFFF"></path>
                    <circle id="Oval" stroke="#333333" stroke-width="0.75" fill="#FED500" cx="28" cy="8" r="7"></circle>
                    <polygon id="Star" fill="#333333" points="28 10.0456659 25.0610737 12.045085 26.0544561 8.63214553 23.2447174 6.45491503
                        26.7975877 6.3450215 28 3 29.2024123 6.3450215 32.7552826 6.45491503 29.9455439 8.63214553 30.9389263 12.045085"></polygon>
                </g>
            </svg>`;

            return {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    svg
                )}`,
            };
        }
        case 'man': {
            const svg = `<?xml version="1.0" encoding="UTF-8"?>
            <svg width="16px" height="41px" viewBox="0 0 16 41" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                <!-- Generator: Sketch 55.2 (78181) - https://sketchapp.com -->
                <title>Contractor / Marker</title>
                <desc>Created with Sketch.</desc>
                <g id="Contractor-/-Marker" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                    <g id="Group-Copy-11" transform="translate(1.000000, 1.000000)" stroke="#333333" stroke-width="0.75">
                        <path d="M11,28 L11,38 C11,38.5522847 10.5522847,39 10,39 L4,39 C3.44771525,39 3,38.5522847 3,38 L3,28 L1,28
                            C0.44771525,28 6.76353751e-17,27.5522847 0,27 L0,15 C-6.76353751e-17,14.4477153 0.44771525,14 1,14 L13,14
                            C13.5522847,14 14,14.4477153 14,15 L14,27 C14,27.5522847 13.5522847,28 13,28 L11,28 Z M7,14 C3.13400675,14
                            0,10.8659932 0,7 C0,3.13400675 3.13400675,0 7,0 C10.8659932,0 14,3.13400675 14,7 C14,10.8659932 10.8659932,14 7,14
                            Z" id="Combined-Shape-Copy" fill="${Config.color[color]}"></path>
                        <path d="M7,39 L7,27.6555786" id="Line-Copy-3" stroke-linecap="round" stroke-linejoin="round"></path>
                        <path d="M3,27.6555786 L3,18.3444214" id="Line-Copy-4" stroke-linecap="round" stroke-linejoin="round"></path>
                        <path d="M11,27.6555786 L11,18.3444214" id="Line-Copy-5" stroke-linecap="round" stroke-linejoin="round"></path>
                    </g>
                </g>
            </svg>`;

            return {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                    svg
                )}`,
            };
        }
        default:
            return null;
    }
}

export const createGoogleMapsLink = (lat, long, place_id) => {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${long}&query_place_id=${place_id}`;
};

export const navigateToGoogleMapsPlaceIdLink = (
    { lat, lng },
    placeAddress,
    placesService,
    placesServiceStatus
) => {
    return placesService?.findPlaceFromQuery(
        {
            query: placeAddress || '',
            fields: ['place_id'],
        },
        (results, status) => {
            if (status === placesServiceStatus.OK && results?.length) {
                const placeId = results[0].place_id;
                const link = createGoogleMapsLink(lat, lng, placeId);
                window.open(link, '_blank');
                return;
            }
            throw new Error('Place not found');
        }
    );
};
